import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { hasOpenOverlay } from '@/app/useHistoryLayer';

/**
 * Day selection of the month grid.
 *
 * Two modes, because one tap must do exactly one thing:
 *
 * - `single` — a tap opens the day's timeline. No selection is armed at all, so
 *   nothing invisible is left behind.
 * - `range` — a tap sets the start, the next one the end. The same day twice is a
 *   valid one-day range.
 *
 * There was a third thing a tap could do — expand an inline detail row — and it
 * is gone. One tap, one outcome, decided by the mode alone.
 */

export type SelectionMode = 'single' | 'range';

export type MonthSelection =
  | { mode: 'idle' }
  | { mode: 'anchor'; day: number }
  | { mode: 'range'; startDay: number; endDay: number };

type SelectionAction =
  | { type: 'tap'; day: number; selectionMode: SelectionMode }
  | { type: 'reset' };

const IDLE: MonthSelection = { mode: 'idle' };

export function reduceSelection(state: MonthSelection, action: SelectionAction): MonthSelection {
  if (action.type === 'reset') return IDLE;
  // Single-day mode never holds a selection; the tap opens the timeline instead.
  if (action.selectionMode === 'single') return IDLE;

  switch (state.mode) {
    case 'idle':
      return { mode: 'anchor', day: action.day };
    case 'anchor':
      // The same day twice is a one-day range. It used to cancel, which made
      // "assign this single day" unreachable from the grid.
      return {
        mode: 'range',
        startDay: Math.min(state.day, action.day),
        endDay: Math.max(state.day, action.day),
      };
    case 'range':
      return { mode: 'anchor', day: action.day };
  }
}

export interface MonthSelectionApi {
  selectionMode: SelectionMode;
  setSelectionMode: (mode: SelectionMode) => void;
  selection: MonthSelection;
  tap: (day: number) => void;
  reset: () => void;
}

const MODE_STORAGE_KEY = 'bk.monthSelectionMode';

function readStoredMode(): SelectionMode {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === 'single' || stored === 'range') return stored;
  } catch {
    // Blocked storage just means the choice does not outlive the session.
  }
  return 'single';
}

export function useMonthSelection(year: number, month: number): MonthSelectionApi {
  const [selectionMode, setSelectionModeState] = useState<SelectionMode>(readStoredMode);
  const [selection, dispatch] = useReducer(reduceSelection, IDLE);

  const tap = useCallback(
    (day: number) => dispatch({ type: 'tap', day, selectionMode }),
    [selectionMode],
  );
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  const setSelectionMode = useCallback((next: SelectionMode) => {
    setSelectionModeState(next);
    // Switching mode must not leave a half-finished selection behind.
    dispatch({ type: 'reset' });
    try {
      localStorage.setItem(MODE_STORAGE_KEY, next);
    } catch {
      // See readStoredMode.
    }
  }, []);

  // A selection only ever refers to the visible month.
  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [year, month]);

  useEffect(() => {
    if (selection.mode === 'idle') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // A dialog or sheet on top owns Escape. Without this guard, dismissing the
      // timeline also cleared the grid behind it.
      if (hasOpenOverlay()) return;
      dispatch({ type: 'reset' });
    };
    // Capture phase, so this runs before the dialog libraries' own document-level
    // handlers. React flushes keydown synchronously, so in the bubble phase the
    // overlay would already have closed and unregistered itself, and the guard
    // above would read an empty stack and clear the grid anyway.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [selection.mode]);

  return useMemo(
    () => ({
      selectionMode,
      setSelectionMode,
      selection,
      tap,
      reset,
    }),
    [selectionMode, setSelectionMode, selection, tap, reset],
  );
}

/** How a cell participates in the current selection. */
export type DaySelectionRole = 'end' | 'between' | null;

export function daySelectionRole(selection: MonthSelection, day: number): DaySelectionRole {
  switch (selection.mode) {
    case 'idle':
      return null;
    case 'anchor':
      return selection.day === day ? 'end' : null;
    case 'range':
      if (day === selection.startDay || day === selection.endDay) return 'end';
      return day > selection.startDay && day < selection.endDay ? 'between' : null;
  }
}
